/**
 * Manager v2 switch (web).
 *
 * OWNER DECISION 2026-10-01: ON for phone and tablet widths (< 1024 CSS px); desktop (≥ 1024) stays
 * on the classic admin for now. For the three manager roles only (event_manager, operations_manager,
 * admin — admin follows the same rule: the v2 route table, nav and Finance hub already handle admin,
 * and every admin-only page is not ported, so it keeps falling through to the classic admin layout).
 *
 * The default is SERVER-CONTROLLED so it can be turned off without a deploy: GET /api/user/profile
 * carries `uiFlags.managerV2Web` from the env var MANAGER_V2_WEB (server/lib/uiFlags.ts; unset = on).
 * To switch it off: set MANAGER_V2_WEB=off in Host A's .env, then
 * `pm2 reload ecosystem.config.cjs --update-env && pm2 save` — no build. A profile without the field
 * (an older server, a cached user) counts as ON, which is the owner's decision.
 *
 * Per-browser override, kept from the staff preview: `?mgr=v2` persists `localStorage['mgr:v2']='1'`
 * (v2 at ANY width, desktop included), `?mgr=v1` persists '0' (classic at any width). A stored choice
 * beats the default; the URL beats both.
 *
 * ⛔ Clients (and every other role) NEVER reach the manager shell: a non-manager role returns false
 * before storage or the URL is even read, so `?mgr=v2` in a client's browser does nothing and writes
 * nothing. App.tsx calls this only inside its admin and event_manager/operations_manager branches as a
 * second fence.
 *
 * Pure functions of (role, url, storage, default) so they are safe during render and unit-tested
 * without a DOM (lib/app/__tests__/wiring.test.ts, managerV2Default.test.ts).
 */
/** The old build-time constant. Kept false: the default now comes from managerV2Default() below. */
export const MANAGER_V2 = false;

/** Widths BELOW this (CSS px) are phone/tablet and get v2 by default; at or above it, the classic. */
export const MANAGER_V2_DESKTOP_MIN = 1024;
/** The media query for "phone or tablet" — the one place the breakpoint is written as CSS. */
export const MANAGER_V2_NARROW_QUERY = `(max-width: ${MANAGER_V2_DESKTOP_MIN - 0.02}px)`;

/**
 * What a manager with no stored choice gets: v2 on a narrow viewport while the server switch is on.
 * `serverOn` is `user.uiFlags?.managerV2Web`; undefined (field absent) = on. Only an explicit false
 * turns it off.
 */
export function managerV2Default(serverOn: boolean | null | undefined, narrow: boolean): boolean {
  return serverOn !== false && narrow;
}

export const MANAGER_V2_STORAGE_KEY = "mgr:v2";

export const MANAGER_V2_ROLES = ["event_manager", "operations_manager", "admin"] as const;

type StorageLike = Pick<Storage, "getItem" | "setItem">;

export function isManagerV2Role(role: unknown): boolean {
  return typeof role === "string" && (MANAGER_V2_ROLES as readonly string[]).includes(role);
}

export function resolveManagerV2(
  role: string | null | undefined,
  search: string,
  storage: StorageLike | null,
  fallback: boolean = MANAGER_V2,
): boolean {
  if (!isManagerV2Role(role)) return false;
  const param = new URLSearchParams(search).get("mgr");
  if (param === "v2" || param === "v1") {
    try {
      storage?.setItem(MANAGER_V2_STORAGE_KEY, param === "v2" ? "1" : "0");
    } catch {
      /* private mode / quota: the URL still decides this page view */
    }
    return param === "v2";
  }
  try {
    const stored = storage?.getItem(MANAGER_V2_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    /* unreadable storage: the default */
  }
  return fallback;
}

export function isManagerV2Enabled(
  role: string | null | undefined,
  opts: { serverOn?: boolean | null; narrow: boolean },
): boolean {
  if (typeof window === "undefined") return false;
  if (!isManagerV2Role(role)) return false;
  let storage: StorageLike | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  return resolveManagerV2(role, window.location.search, storage, managerV2Default(opts.serverOn, opts.narrow));
}

/** True while the viewport is phone/tablet width. Re-renders on rotation/resize across 1024px. */
export function isNarrowViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MANAGER_V2_NARROW_QUERY).matches;
}

export function subscribeNarrowViewport(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const mq = window.matchMedia(MANAGER_V2_NARROW_QUERY);
  mq.addEventListener?.("change", onChange);
  return () => mq.removeEventListener?.("change", onChange);
}
